import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useDynamicPhoneErrorToast } from './useDynamicPhoneErrorToast';
import { useApplicationContext } from '../containers/ApplicationContext';
import { setDynamicPhones } from '../store/dynamicPhones';
import { mapCalltrackingPhoneToValidPhone, requestOfferDynamicPhone } from '../utils/dynamicCalltracking';

interface IRequestParams {
  offerId: number;
  clickId: string;
  hasStaticPhones: boolean;
  /** Событие деградации: КТ не ответил, показана статика */
  onDegradation(): void;
}

interface IDynamicPhoneFlow {
  /** false — показывать нечего, блок раскрывать нельзя */
  requestPhone(params: IRequestParams): Promise<boolean>;
}

/**
 * ДинКТ вторички (SEL-998): запрос подменного номера по клику.
 * Успех кладёт номера в store (их показывают все блоки контактов карточки),
 * иначе фоллбэк на статику, а без статики — тост-ошибка.
 */
export function useDynamicPhoneFlow(): IDynamicPhoneFlow {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const showPhoneErrorToast = useDynamicPhoneErrorToast();

  const requestPhone = useCallback(
    async ({ offerId, clickId, hasStaticPhones, onDegradation }: IRequestParams): Promise<boolean> => {
      const result = await requestOfferDynamicPhone({ offerId, clickId }, { config, httpApi, logger });

      if (result?.status === 'ok') {
        dispatch(setDynamicPhones(result.phones.map(mapCalltrackingPhoneToValidPhone)));

        return true;
      }

      if (!hasStaticPhones) {
        logger.warning('OfferDynamicCalltracking: номер недоступен, показан тост-ошибка', {
          offerId,
          clickId,
          status: result?.status ?? null,
        });
        showPhoneErrorToast();

        return false;
      }

      if (result?.status === 'failedToAllocatePhone') {
        // TODO CD-287168: по контракту реальный номер не раскрывается — ждём чат-фоллбэк
        logger.warning('OfferDynamicCalltracking: failedToAllocatePhone, показана статика', { offerId, clickId });
      } else {
        onDegradation();
      }

      return true;
    },
    [config, dispatch, httpApi, logger, showPhoneErrorToast],
  );

  return { requestPhone };
}
